/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useState, Fragment, useEffect, useRef } from 'react'
import { SimpleInput } from '../helpers/simple-input'
import { Button, Select } from '../../atoms'
import isDeepEqual from 'fast-deep-equal/react'
import InputAlert from '../helpers/input-alert'
import { AddressFormComponentProps, useAddressFormProps, AddressLookupProps } from './types'

const AddressFormComponent = (props: AddressFormComponentProps) => {
  const {
    addressIndex,
    label,
    lookup,
    onChange,
    error,
    formVariant,
    defaultValue = {},
    minimumRequiredYears,
  } = props
  const [formValue, Form] = useAddressForm({
    defaultState: {
      county: '',
      number: '',
      buildingname: '',
      subbuildingname: '',
      street: '',
      posttown: '',
      postcode: '',
      summaryline: '',
      yearsAtAddress: '0',
      display: true,
      ...defaultValue,
    },
    addressIndex,
    label,
    lookup,
    error,
    formVariant,
    minimumRequiredYears,
  })
  const formRef = useRef(formValue)

  if (!isDeepEqual(formRef.current, formValue)) {
    formRef.current = formValue
  }

  useEffect(() => {
    onChange(addressIndex, { ...formValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef.current])

  return <Fragment>{Form}</Fragment>
}

const useAddressForm = (props: useAddressFormProps) => {
  const { defaultState, addressIndex, label, lookup, error, formVariant, minimumRequiredYears } =
    props
  const [state, setState] = useState(defaultState)

  return [
    state,
    <AddressLookup
      key={addressIndex}
      data={state}
      label={label}
      addressIndex={addressIndex}
      lookup={lookup}
      setData={setState}
      defaultState={defaultState}
      error={error}
      formVariant={formVariant}
      minimumRequiredYears={minimumRequiredYears}
    />,
    setState,
  ]
}

const AddressLookup = (props: AddressLookupProps) => {
  const {
    setData,
    data,
    addressIndex,
    label,
    lookup,
    defaultState,
    error,
    formVariant,
    minimumRequiredYears,
  } = props
  const variantPath = (element) => `elements.form.variants.${formVariant}.${element}`
  const [postcode, setPostcode] = useState('')
  const [addresses, setAddresses] = useState(defaultState)
  // postcode | static | edit
  const [addressView, setAddressView] = useState(defaultState?.postcode ? 'static' : 'postcode')

  const returnData = (key, returnValue) => {
    setData((prevData) => ({
      ...prevData,
      [key]: returnValue,
    }))
  }

  const handleOnAddressSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== 'not-listed') {
      // Spread so we keep years at the address
      setData({
        ...data,
        ...addresses.find((a) => a.summaryline === e.target.value),
      })
      setAddressView('static')
    } else {
      setAddressView('edit')
    }
  }

  const handleViewChange = (view) => {
    if (view === 'postcode') {
      setData(defaultState)
      setAddresses([])
    }

    setAddressView(view)
  }

  const postCodeInput = useRef(null)
  const postcodeError =
    error &&
    ((data.premise === '' && data.number === '') || data.posttown === '' || data.postcode === '')

  const postcodeView = (
    <div>
      <div>
        <If condition={addressIndex > 0}>
          <label htmlFor='postcode' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Please enter your previous address
          </label>
        </If>
        <SimpleInput
          defaultValue={postcode}
          ref={postCodeInput}
          name={'postcode'}
          hasError={postcodeError}
          placeholder={'Search by postcode or address'}
        />
      </div>
      <Button
        onClick={() => {
          const postCode = postCodeInput.current.value
          setPostcode(postCode)
          lookup(postCodeInput.current.value, setAddresses)
        }}
        variant={'secondary--eligibility'}
      >
        Find address
      </Button>

      <If condition={addresses.length > 0}>
        <div sx={{ mv: 'sm' }}>
          <Select
            name={'addresslist'}
            placeholder={'Please select your address'}
            options={[
              { text: 'Please select your address' },
              { text: "My address isn't listed", value: 'not-listed' },
              ...addresses.map((address) => {
                return { text: address.summaryline, value: address.summaryline }
              }),
            ]}
            onChange={handleOnAddressSelectChange}
            onBlur={() => {}}
            value={''}
            hasError={postcodeError}
          />
        </div>
      </If>

      <If condition={postcodeError}>
        <InputAlert type='alert' sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}>
          Please select your address
        </InputAlert>
      </If>
    </div>
  )

  const staticView = (
    <div>
      <Button variant='secondary--eligibility' onClick={() => handleViewChange('postcode')}>
        Search for a different address
      </Button>
      <div sx={{ mt: 'sm', variant: 'elements.form.address-lookup.display' }}>
        <div>{data.subbuildingname}</div>
        <div>{data.buildingname}</div>
        <div>
          {data.number} {data.street}
        </div>
        <div>{data.posttown}</div>
        <div>{data.county}</div>
        <div>{data.postcode}</div>
        <Button variant='secondary--eligibility' onClick={() => handleViewChange('edit')}>
          Edit address
        </Button>
      </div>
    </div>
  )

  const editView = (
    <Fragment>
      <Button variant='secondary--eligibility' onClick={() => handleViewChange('postcode')}>
        Back to address finder
      </Button>
      <div sx={{ mt: 'sm', variant: 'elements.form.address-lookup.edit-container' }}>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='number' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Building number
          </label>
          <SimpleInput
            name={'number'}
            onChange={(e) => returnData('number', e.currentTarget.value)}
            value={data.number}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='buildingname' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Building name
          </label>
          <SimpleInput
            name={'buildingname'}
            onChange={(e) => returnData('buildingname', e.currentTarget.value)}
            value={data.buildingname}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='subbuildingname' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Flat number
          </label>
          <SimpleInput
            name={'subbuildingname'}
            onChange={(e) => returnData('subbuildingname', e.currentTarget.value)}
            value={data.subbuildingname}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='street' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Street
          </label>
          <SimpleInput
            name={'street'}
            onChange={(e) => returnData('street', e.currentTarget.value)}
            value={data.street}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='posttown' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Town or city
          </label>
          <SimpleInput
            name={'posttown'}
            onChange={(e) => returnData('posttown', e.currentTarget.value)}
            value={data.posttown}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='county' sx={{ variant: 'elements.form.address-lookup.label' }}>
            County
          </label>
          <SimpleInput
            name={'county'}
            onChange={(e) => returnData('county', e.currentTarget.value)}
            value={data.county}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='postcode' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Postcode
          </label>
          <SimpleInput
            name={'postcode'}
            onChange={(e) => returnData('postcode', e.currentTarget.value)}
            value={data.postcode}
          />
        </div>
        <input type='hidden' value={data.uprn} />
      </div>
    </Fragment>
  )

  const TimeAtAddress = () => (
    <div>
      <label htmlFor='postcode' sx={{ mt: 'sm', variant: 'elements.form.variants.default.label' }}>
        {`How many years ${
          addressIndex === 0 ? 'have you lived' : 'did you live'
        } at this address?`}
      </label>
      <Select
        name={'yearslist'}
        placeholder={'Please select'}
        options={[
          { text: 'Please select', value: 0 },
          ...[...Array(16).keys()].map((i) => {
            const index = i + 1
            if (index === 1) {
              return { text: '1 year or less', value: index }
            } else if (index === 16) {
              return { text: '16+ years', value: index }
            } else {
              return { text: `${index} years`, value: index }
            }
          }),
        ]}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          returnData('yearsAtAddress', e.currentTarget.value)
        }
        onBlur={() => {}}
        value={data.yearsAtAddress}
        hasError={error && (data.yearsAtAddress === '' || data.yearsAtAddress === '0')}
      />

      <If condition={error && (data.yearsAtAddress === '' || data.yearsAtAddress === '0')}>
        <InputAlert type='alert' sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}>
          Please select how many years you&apos;ve lived at this address
        </InputAlert>
      </If>
    </div>
  )

  return (
    <div sx={{ variant: 'elements.form.address-lookup.container' }}>
      <If condition={label}>
        <h2>{label}</h2>
      </If>
      <If condition={defaultState.display}>
        {addressView === 'postcode'
          ? postcodeView
          : addressView === 'static'
          ? staticView
          : editView}
        <TimeAtAddress />
      </If>
      <If condition={addressIndex === 0}>
        <p>
          {` If you have relocated within the past ${minimumRequiredYears} years, we may need your previous address to
          help us locate your credit file.`}
        </p>
      </If>
    </div>
  )
}

export default AddressFormComponent
